import { HeartIcon } from '@src/components/icons/HeartIcon';
import { PaperPlaneIcon } from '@src/components/icons/PaperPlaneIcon';
import { ArrowRightIcon } from '@src/components/icons/ArrowRightIcon';
import { ClockIcon } from '@src/components/icons/ClockIcon';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { Button, Hint, Input, Select } from '@src/components';
import { RefreshIcon } from '@src/components/icons/RefreshIcon';
import { ParticipantFace } from '@src/components/faces/ParticipantFace';
import { AdminDataTable } from './AdminDataTable';
import { GiftInvitationHistory } from './GiftInvitationHistory';
import './AdminPayments.css';
import './RandomUserGifts.css';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyTag } from './CopyTag';

type Gift = {
  id: number;
  userId: number;
  username: string | null;
  boxName: string | null;
  recipient: { name: string; picture: string | null; avatar: string | null; frameName: string | null; isOrganizer: boolean; isParticipant: boolean } | null;
  boxId: number;
  status: string;
  assignedAt: string | null;
  seenAt: string | null;
  thankedAt: string | null;
  thankYouMessage: string | null;
  clicked: boolean;
  giftsBought: number;
  checkoutClicks: number; lastCheckoutAt: string | null;
  pendingPayments: number; failedPayments: number; paidPayments: number; lastPaidAt: string | null;
  principalKind: 'guest' | 'account' | null;
  lastActiveAt: string | null;
  lastLoginAt: string | null;
};
type Dashboard = { boxCounts?: { status: string; count: number }[]; summary?: { wizardTotal?: number; wizardPurchased?: number; exposed: number; total: number; purchased: number; purchases: number; buyers: number }; counts: { status: string; count: number }[]; funnel: Record<string, number>; packages: import('./UserGiftFunnels').GiftPackageStats[] };
export function RandomUserGifts({ onDenied }: { onDenied: () => void }) {
  const { t, i18n } = useTranslation();
  const time = (value: string | null) => value ? new Date(value).toLocaleString(i18n.language) : '—';
  const [credits, setCredits] = useState('1000');
  const [wizardCredits, setWizardCredits] = useState('1');
  const [userIds, setUserIds] = useState('');
  const [senderUserId, setSenderUserId] = useState(import.meta.env.DEV ? '' : '10318125');
  const [message, setMessage] = useState('');
  const senderValid = !senderUserId.trim() || (Number.isSafeInteger(Number(senderUserId)) && Number(senderUserId) > 0 && Number(senderUserId) <= 2147483647);
  const sender = { senderUserId: senderUserId.trim() ? Number(senderUserId) : null, message };
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [filters, setFilters] = useState({ status: 'all', thanked: 'all', clicked: 'all', bought: 'all', frame: 'all' });
  const [tablePageSize, setTablePageSize] = useState(25);
  const [cursor, setCursor] = useState<number | null>(null);
  const [cursorStack, setCursorStack] = useState<(number | null)[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [tableRevision, setTableRevision] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const filterQuery = new URLSearchParams({ ...filters, pageSize: String(tablePageSize), ...(cursor ? { before: String(cursor) } : {}) }).toString();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [senderError, setSenderError] = useState(false);
  const [result, setResult] = useState('');
  const [historyRevision, setHistoryRevision] = useState(0);
  async function api(path: string, body?: unknown) {
    const res = await fetch(`/api/admin/santa-gifts/${path}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...(body === undefined
        ? {}
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }),
    });
    if ([401, 403].includes(res.status)) onDenied();
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw Error(body?.error?.message || 'Request failed');
    }
    return res.json();
  }
  async function refresh() {
    setDashboard(await api('credits'));
    setTableRevision(value => value + 1);
    setHistoryRevision(value => value + 1);
  }
  useEffect(() => {
    let active = true;
    let pending = false;
    const update = async () => {
      if (pending) return;
      pending = true;
      try {
        const stats = await api('credits');
        if (active) {
          setDashboard(stats);
          setTableRevision(value => value + 1);
        }
      } catch {
        if (active) setError(true);
      } finally {
        pending = false;
      }
    };
    void update();
    const timer = window.setInterval(() => { void update(); }, 60_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
    // Load once; changing the authorization callback must not reset edited controls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    let active = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reflect the new external request before its response arrives.
    setTableLoading(true);
    void api(`users?${filterQuery}`).then(history => {
      if (active) { setGifts(history.gifts); setNextCursor(history.nextCursor ?? null); }
    }).catch(() => { if (active) setError(true); }).finally(() => { if (active) setTableLoading(false); });
    return () => { active = false; };
    // The request is keyed by filters, cursor, and explicit/automatic refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery, tableRevision]);
  async function run(fn: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    setError(false);
    setSenderError(false);
    setResult('');
    try {
      await fn();
      await refresh();
    } catch (cause) {
      if (cause instanceof Error && cause.message === 'Gift sender not found') setSenderError(true);
      else setError(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="admin-payments user-gifts-admin" data-testid="admin-user-gifts">
      <header className="user-gifts-admin__header">
        <div className="user-gifts-admin__heading">
          <h2>{t('admin.userGifts.title')}</h2>
          <Button size="small" variant="secondary" icon={<RefreshIcon />} disabled={busy} loading={busy} onClick={() => void run(async () => {})} testId="admin-user-gifts-refresh">{t('admin.payments.refresh')}</Button>
        </div>
      </header>
      <div className="user-gifts-admin__overview">
        <dl className="user-gifts-admin__pools">
          {(['users', 'boxes'] as const).map(pool => <div key={pool}>
            <dt>{t(pool === 'users' ? 'giftCredits.userPool' : 'giftCredits.wizardLabel')}</dt>
            <dd>{(pool === 'users' ? dashboard?.counts : dashboard?.boxCounts)?.find(c => c.status === 'credit')?.count ?? 0}</dd>
            <span>{t('giftCredits.credit')}</span>
          </div>)}
        </dl>
        <div className="user-gifts-admin__details">
          <dl className="user-gifts-admin__stats" data-testid="gift-credit-totals">
            {(['total', 'wizardTotal', 'purchased', 'wizardPurchased', 'buyers', 'purchases', 'exposed'] as const).map(key => <div key={key} title={t(`giftCredits.summary.${key}Hint`, { defaultValue: '' })}>
              <dt>{t(`giftCredits.summary.${key}`)}</dt><dd>{dashboard?.summary?.[key] ?? 0}</dd>
            </div>)}
          </dl>
          <div className="user-gifts-admin__status-scroll">
            <table className="user-gifts-admin__status-table">
              <thead><tr><th scope="col">{t('giftCredits.pool')}</th>{['queued', 'locked', 'assigned', 'delivered'].map(status => <th scope="col" key={status}>{t(`giftCredits.${status}`)}</th>)}</tr></thead>
              <tbody>{(['users', 'boxes'] as const).map(pool => <tr key={pool}>
                <th scope="row">{t(pool === 'users' ? 'giftCredits.userPool' : 'giftCredits.wizardLabel')}</th>
                {['queued', 'locked', 'assigned', 'delivered'].map(status => <td key={status}>{pool === 'boxes' && status === 'queued' ? '—' : (pool === 'users' ? dashboard?.counts : dashboard?.boxCounts)?.find(c => c.status === status)?.count ?? 0}</td>)}
              </tr>)}</tbody>
            </table>
          </div>
        </div>
      </div>
      {senderError && <Hint tone="error">{t('giftCredits.senderNotFound')}</Hint>}
      {error && <Hint tone="error">{t('admin.userGifts.error')}</Hint>}
      <div className="user-gifts-admin__compose">
      <div className="user-gifts-admin__sender">
        <Input label={t('giftCredits.senderUserId')} inputMode="numeric" value={senderUserId} onValueChange={setSenderUserId} disabled={busy} />
        <Input label={t('giftCredits.message')} maxLength={300} value={message} onValueChange={setMessage} disabled={busy} />
      </div>
      <div className="user-gifts-admin__actions">
        <div className="user-gifts-admin__add-credits">
          <Input
            label={t('giftCredits.userPool')}
            type="number"
            step="1"
            min="1"
            max="10000"
            value={credits}
            onValueChange={setCredits}
          />
          <Button
            disabled={
              busy || !senderValid ||
              !Number.isInteger(Number(credits)) ||
              Number(credits) < 1 ||
              Number(credits) > 10000
            }
            onClick={() =>
              void run(async () => {
                await api('credits', { count: Number(credits), ...sender });
                setResult(t('giftCredits.added', { count: Number(credits) }));
              })
            }
          >
            {t('giftCredits.add')}
          </Button>
        </div>
        <div className="user-gifts-admin__add-credits">
          <Input
            label={t('giftCredits.wizardLabel')}
            type="number"
            step="1"
            min="1"
            max="10000"
            value={wizardCredits}
            onValueChange={setWizardCredits}
          />
          <Button
            disabled={
              busy || !senderValid ||
              !Number.isInteger(Number(wizardCredits)) ||
              Number(wizardCredits) < 1 ||
              Number(wizardCredits) > 10000
            }
            onClick={() =>
              void run(async () => {
                await api('credits', { count: Number(wizardCredits), ...sender, audience: 'boxes' });
                setResult(t('giftCredits.wizardAdded', { count: Number(wizardCredits) }));
              })
            }
          >
            {t('giftCredits.add')}
          </Button>
        </div>
        <div className="user-gifts-admin__target-user">
          <Input label={t('giftCredits.userIds')} value={userIds} onValueChange={setUserIds} />
          <Button
            disabled={busy || !senderValid || !userIds.trim()}
            onClick={() =>
              void run(async () => {
                const ids = userIds
                  .trim()
                  .split(/[\s,;]+/)
                  .map(Number);
                const data = await api('send-users', { userIds: ids, ...sender });
                setResult(
                  t('admin.userGifts.result', {
                    sent: data.gifts.length,
                    skipped: data.skippedUserIds.length,
                  }),
                );
              })
            }
          >
            {t('giftCredits.invite')}
          </Button>
        </div>
      </div>

      </div>
      {result && <p role="status">{result}</p>}
      <h3>{t('admin.userGifts.history')}</h3>
      <div className="user-gifts-admin__filters">
        {(Object.keys(filters) as (keyof typeof filters)[]).map(key => (
          <label key={key}>{t(`giftCredits.filters.${key}`)}
            <Select value={filters[key]} onValueChange={value => { setFilters(previous => ({ ...previous, [key]: value })); setCursor(null); setCursorStack([]); }}
              aria-label={t(`giftCredits.filters.${key}`)} options={[
                { value: 'all', label: t('giftCredits.filters.all') },
                ...(key === 'status' ? ['locked', 'assigned', 'gifted', 'delivered'].map(value => ({ value, label: t(`giftCredits.${value}`, { defaultValue: value }) })) :
                  ['yes', 'no'].map(value => ({ value, label: t(`admin.userGifts.${value}`) }))),
              ]} />
          </label>
        ))}
      </div>
      <div className="admin-payments__scroll" aria-busy={tableLoading}>
        <AdminDataTable key={filterQuery} paginate={false} pageSize={tablePageSize} onPageSizeChange={size => { setTablePageSize(size); setCursor(null); setCursorStack([]); }} pagination={{ previousLabel: t('admin.payments.previous'), nextLabel: t('admin.payments.next'), pageLabel: page => t('admin.payments.page', { page }), page: cursorStack.length + 1, pageCount: cursorStack.length + (nextCursor ? 2 : 1), onChange: page => {
          if (tableLoading) return;
          if (page > cursorStack.length + 1 && nextCursor) { setCursorStack(previous => [...previous, cursor]); setCursor(nextCursor); }
          else if (page < cursorStack.length + 1) { setCursor(cursorStack[page - 1] ?? null); setCursorStack(previous => previous.slice(0, page - 1)); }
        } }}>
          <thead>
            <tr>
              {['gift', 'recipient', 'box', 'activity', 'status', 'assigned', 'opened', 'actions'].map((k) => (
                <th key={k}>{t(k === 'status' ? 'giftCredits.status' : k === 'frame' ? 'giftCredits.filters.frame' : `admin.userGifts.${k}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {gifts.map((g) => (
              <tr key={g.id}>
                <td>
                  <CopyTag
                    value={String(g.id)}
                    prefix="gift"
                    label={t('admin.userGifts.copy.gift')}
                  />
                </td>
                <td>
                  <div className="user-gifts-admin__recipient">
                    <span className="user-gifts-admin__avatar"><ParticipantFace picture={g.recipient?.picture || 'unknown'} card={g.recipient || {}} index={g.userId} /></span>
                    <div className="user-gifts-admin__identity">
                      <strong>{g.recipient?.name || g.username || '—'}</strong>
                      {g.username && g.username !== g.recipient?.name && <span>{g.username}</span>}
                      <small>{g.principalKind ? t(`admin.userGifts.${g.principalKind}`) : '—'}</small>
                  <CopyTag
                    value={String(g.userId)}
                    prefix="user"
                    label={t('admin.userGifts.copy.user')}
                  />
                    </div>
                  </div>
                </td>
                <td>
                  {g.boxName && <div className="user-gifts-admin__box-name">{g.boxName}</div>}
                  <div className="user-gifts-admin__role">{[g.recipient?.isOrganizer && t('admin.userGifts.organizer'), g.recipient?.isParticipant && t('admin.userGifts.participant')].filter(Boolean).join(' · ') || '—'}</div>
                  <CopyTag
                    value={String(g.boxId)}
                    prefix="box"
                    label={t('admin.userGifts.copy.box')}
                  />
                </td>
                <td className="user-gifts-admin__activity">
                  <div>{t('admin.userGifts.lastActive')}: {time(g.lastActiveAt)}</div>
                  <div>{t('admin.userGifts.lastLogin')}: {time(g.lastLoginAt)}</div>
                </td>
                <td>{t(`giftCredits.${g.status}`, { defaultValue: g.status })}</td>
                <td>{time(g.assignedAt)}</td>
                <td>{time(g.seenAt)}</td>
                <td>
                  <div className="user-gifts-admin__engagement">
                    {[
                      { key: 'thanks', Icon: HeartIcon, value: g.thankedAt ? t('admin.userGifts.yes') : '—', detail: g.thankYouMessage || (g.thankedAt ? time(g.thankedAt) : '—'), active: !!g.thankedAt },
                      { key: 'clicked', Icon: PaperPlaneIcon, value: g.clicked ? t('admin.userGifts.yes') : '—', detail: g.clicked ? t('admin.userGifts.yes') : '—', active: g.clicked },
                      { key: 'checkout', Icon: ArrowRightIcon, value: g.checkoutClicks || '—', detail: `${t('admin.userGifts.clickCount', { count: g.checkoutClicks })} · ${time(g.lastCheckoutAt)}`, active: g.checkoutClicks > 0 },
                      { key: 'paymentAttempts', Icon: ClockIcon, value: (g.pendingPayments + g.failedPayments) || '—', detail: `${t('admin.userGifts.pendingCount', { count: g.pendingPayments })} · ${t('admin.userGifts.failedCount', { count: g.failedPayments })}`, active: !!(g.pendingPayments || g.failedPayments) },
                      { key: 'bought', Icon: GiftIcon, value: g.giftsBought || '—', detail: `${t('admin.userGifts.giftsBought', { count: g.giftsBought })} · ${t('admin.userGifts.paidCount', { count: g.paidPayments })} · ${time(g.lastPaidAt)}`, active: g.giftsBought > 0 },
                    ].map(({ key, Icon, value, detail, active }) => <span key={key} className={`user-gifts-admin__metric${active ? ' is-active' : ''}`} tabIndex={0} aria-label={`${t(`admin.userGifts.${key}`)}: ${detail}`}>
                      <Icon /><strong>{value}</strong>
                      <span className="user-gifts-admin__metric-tooltip" role="tooltip"><b>{t(`admin.userGifts.${key}`)}</b><br />{detail}</span>
                    </span>)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </AdminDataTable>
      </div>
      <GiftInvitationHistory key={historyRevision} onDenied={onDenied} onReturned={() => void run(refresh)} />
    </section>
  );
}
